<template>
  <dialog class="modal" :class="{ 'modal-open': open }">
    <div class="modal-box">
      <h3 class="text-lg font-bold">Cập nhật thông tin người dùng</h3>
      <form @submit="onSubmit">
        <div class="space-y-4 py-4">
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Tên đăng nhập</legend>
            <input
              v-model="username"
              type="text"
              class="input input-bordered w-full"
              :class="{ 'input-error': errors.username }"
              placeholder="Nhập tên đăng nhập"
            />
            <p v-if="errors.username" class="label text-error">
              {{ errors.username }}
            </p>
          </fieldset>

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Mật khẩu</legend>
            <input
              v-model="password"
              type="password"
              class="input input-bordered w-full"
              :class="{ 'input-error': errors.password }"
              placeholder="Để trống nếu không đổi"
            />
            <p v-if="errors.password" class="label text-error">
              {{ errors.password }}
            </p>
          </fieldset>

          <fieldset class="fieldset" v-if="password">
            <legend class="fieldset-legend">Xác nhận mật khẩu</legend>
            <input
              v-model="confirmPassword"
              type="password"
              class="input input-bordered w-full"
              :class="{ 'input-error': errors.confirmPassword }"
              placeholder="Nhập lại mật khẩu"
            />
            <p v-if="errors.confirmPassword" class="label text-error">
              {{ errors.confirmPassword }}
            </p>
          </fieldset>

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Trạng thái</legend>
            <label class="label cursor-pointer justify-start gap-4">
              <span class="label-text">Kích hoạt</span>
              <input
                v-model="enable"
                type="checkbox"
                class="toggle toggle-primary"
              />
            </label>
          </fieldset>

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Vai trò</legend>
            <select
              v-model="role"
              class="select select-bordered w-full"
              :class="{ 'select-error': errors.role }"
            >
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
            <p v-if="errors.role" class="label text-error">
              {{ errors.role }}
            </p>
          </fieldset>
        </div>
        <div class="modal-action">
          <button type="button" class="btn" @click="closeModal">Đóng</button>
          <button
            type="submit"
            class="btn btn-primary ml-2"
            :disabled="isUpdating"
          >
            <span v-if="isUpdating" class="loading loading-spinner"></span>
            Cập nhật
          </button>
        </div>
      </form>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="closeModal">close</button>
    </form>
  </dialog>
</template>

<script setup lang="ts">
import { type User } from "@/api/user";
import { useUpdateUserMutation } from "@/composables/useUser";
import { useForm } from "vee-validate";
import { watch } from "vue";
import { toast } from "vue3-toastify";

const open = defineModel<boolean>("open", { default: false });
const props = defineProps<{ user: User | null }>();

const { defineField, handleSubmit, errors, resetForm, setValues } = useForm({
  validationSchema: {
    username: "required|min:4|max:50",
    password: "min:8|max:64|password_complex",
    confirmPassword: "confirmed:@password",
    role: "required",
  },
});

const [username] = defineField("username", { label: "Tên đăng nhập" });
const [password] = defineField("password", { label: "Mật khẩu" });
const [confirmPassword] = defineField("confirmPassword", {
  label: "Xác nhận mật khẩu",
});
const [enable] = defineField("enable");
const [role] = defineField("role");

const { mutate: updateUser, isPending: isUpdating } = useUpdateUserMutation();

const onSubmit = handleSubmit((values) => {
  if (!props.user) return;

  updateUser(
    {
      id: props.user.id,
      username: values.username,
      password: values.password || undefined,
      enable: !!values.enable,
      role: values.role || undefined,
    },
    {
      onSuccess: () => {
        toast.success("Cập nhật thông tin thành công!");
        closeModal();
      },
      onError: () => {
        toast.error("Đã xảy ra lỗi khi cập nhật.");
      },
    },
  );
});

const closeModal = () => {
  open.value = false;
  resetForm();
};

watch(
  () => [open.value, props.user] as const,
  ([isOpen, user]) => {
    if (isOpen && user) {
      setValues({
        username: user.username,
        password: "",
        confirmPassword: "",
        enable: user.enable,
        role: user.role || "operator",
      });
    }
  },
  { immediate: true },
);
</script>
